import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const JsonToPython = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const toPythonClass = (name, obj, indent = '') => {
    let classLines = [`${indent}@dataclass`, `${indent}class ${name}:`];
    let nestedClasses = [];

    for (const key in obj) {
      const value = obj[key];
      let pyType = inferType(value);
      const safeKey = key.match(/^[a-zA-Z_][a-zA-Z0-9_]*$/) ? key : `'${key}'`;

      if (pyType === 'object') {
        const className = `${name}_${capitalize(key)}`;
        nestedClasses.push(toPythonClass(className, value, indent));
        classLines.push(`${indent}  ${safeKey}: ${className}`);
      } else {
        classLines.push(`${indent}  ${safeKey}: ${pyType}`);
      }
    }

    if (classLines.length === 2) {
      classLines.push(`${indent}  pass`);
    }

    return [...nestedClasses, ...classLines].join('\n');
  };

  const inferType = (value) => {
    if (Array.isArray(value)) return 'list';
    if (typeof value === 'number') return Number.isInteger(value) ? 'int' : 'float';
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'string') return 'str';
    if (typeof value === 'object' && value !== null) return 'object';
    return 'Any';
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const handleConvert = () => {
    try {
      const json = JSON.parse(inputCode);
      if (typeof json !== 'object') throw new Error('Root must be an object');
      const pythonClass = `from dataclasses import dataclass\n\n` + toPythonClass('MyClass', json);
      setOutputCode(pythonClass);
    } catch (e) {
      setOutputCode("// ❌ Invalid JSON:\n"  + e.message);
    }
  };

    return (
      <div className="formatter-container">
        <h1>JSON ➝ Python Converter </h1>

        {/* Toolbar */}
        <div className="toolbar">
          <button onClick={handleConvert} className="btn primary">
            <FaBrush /> Convert
          </button>
          <button onClick={() => handleDownload("c", "formatted-c")} className="btn success">
            <FaDownload /> Download
          </button>
  		<button onClick={handleClear} className="btn danger">
            <FaTrash /> Clear
          </button>

        </div>

        {/* Editors */}
        <div className="editor-panels">
          <JsonEditorPanel title="Input JSON" language="json" code={inputCode} setCode={setInputCode} />
          <div className="editor-section-empty-div">
          </div>
          <JsonEditorPanel title="Output Python" language="python"  code={outputCode} />
        </div>
		
		<CommonSeoContent
  title="Online JSON to Python Class Converter"
  description="Convert JSON data into Python class structures or dictionaries with jsonconverter.org. Supports nested JSON and generates clean Python dataclasses or dicts."
  keywords="JSON to Python, json to python dict, json to python class, json to dataclass, python code generator, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use JSON to Python Converter?"
  whyUseText="Use our JSON to Python Converter to transform JSON into Python dictionaries or modern data classes with type hints. Perfect for data analysis, FastAPI backends, Django apps, or automation scripts, this tool preserves the structure and data types of your JSON while offering output that’s clean and Pythonic. It handles nesting, lists, and all major JSON types. You can choose between raw `dict` output or structured `@dataclass` models. Everything happens in your browser — no installation or internet access required."
  howToUseSteps={[
    "Paste your JSON into the input editor.",
    "Click Convert to generate Python code.",
    "Choose between dataclass or dict output.",
    "Copy or download the result for your project.",
  ]}
  features={[
    "Generates Python dicts or dataclasses from JSON",
    "Supports nested objects and lists",
    "Output can be used with FastAPI, Pydantic, or plain Python",
    "Client-side — your data stays local",
    "Free, no login or installation needed",
    "Download Python code for offline use",
  ]}
  inputTitle="Input JSON:"
  inputExample={`{
  "id": 101,
  "title": "JSON to Python",
  "tags": ["json", "python", "converter"]
}`}
  outputTitle="Output Python:"
  outputExample={`from dataclasses import dataclass
from typing import List

@dataclass
class Data:
  id: int
  title: str
  tags: List[str]`}
  callToAction="Start converting your JSON to Python classes instantly — no signup required."
/>
      </div>
    );
};

export default JsonToPython;